/**
 * Chakra 3 — Manipura (§8). Element Vuur, kleur geel, mantra RAM (528Hz).
 * Resource-label: Wilskracht. Wereld: "Het Gedoofde Vuur".
 *
 * Let op: Zonnesteen's tekst noemt "Elementkaarten +2", maar het spec-effect is
 * een generieke `buffOutput` (geen type-filter in de engine) — zo overgenomen.
 */
import type { Chakra } from './types';
import { HEADERS } from './headers';

export const chakra3: Chakra = {
  header: HEADERS[3],
  wereld: 'Het Gedoofde Vuur',
  codex:
    'Manipura, "stad van juwelen", zit bij de zonnevlecht. Element Vuur, kleur geel, 10 bloemblaadjes, mantra RAM (528Hz). Levensfase: kindertijd waarin wil en identiteit zich vormen. Levensvraag: mag ik mijn eigen wil hebben? Lichamelijk: spijsverteringsorganen, lever, pancreas, bijnieren (energieverbruik). Tekort: machteloos, geen grenzen, laag zelfbeeld, anderen bepalen je leven. Teveel: dominant, controlerend, woede als enige uitlaatklep, workaholisme/burnout door overcompenseren. Balans: gezonde wilskracht, persoonlijke kracht zonder anderen te overheersen, gezonde grenzen, doelgerichtheid. Herstel: kernkracht, grenzen stellen, zonlicht, doelen stellen en behalen.',

  kaarten: [
    { id: 'ch3-tijgeroog', naam: 'Tijgeroog', type: 's', cost: 1, origin: 3, tekst: '+3 wilskracht, neutraliseert kleineringen', effect: { block: 3, cleanse: 'one' } },
    { id: 'ch3-citrien', naam: 'Citrien', type: 's', cost: 1, origin: 3, tekst: '+3 energie direct', effect: { energy: 3 } },
    { id: 'ch3-gele-jaspis', naam: 'Gele Jaspis', type: 's', cost: 2, origin: 3, tekst: 'Steenkaarten -1 kosten (3 beurten)', effect: { costDownType: { type: 's', amt: 1, turns: 3 } } },
    { id: 'ch3-pyrite', naam: 'Pyrite', type: 's', cost: 2, origin: 3, tekst: 'Bescherming tegen energieverlies (1 beurt)', effect: { cleanse: 'one' } },
    { id: 'ch3-zonnesteen', naam: 'Zonnesteen', type: 's', cost: 3, origin: 3, tekst: 'Elementkaarten +2 effect (2 beurten)', effect: { buffOutput: { amt: 2, turns: 2 } } },
    { id: 'ch3-maag-activatie', naam: 'Maag Activatie', type: 'o', cost: 1, origin: 3, tekst: 'Neutraliseert spijsverteringsaanvallen', effect: { cleanse: 'one' } },
    { id: 'ch3-lever-kracht', naam: 'Lever Kracht', type: 'o', cost: 2, origin: 3, tekst: '+4 HP, verwijder 1 blokkade', effect: { heal: 4, cleanse: 'one' } },
    { id: 'ch3-pancreas-balans', naam: 'Pancreas Balans', type: 'o', cost: 1, origin: 3, tekst: '+2 energie', effect: { energy: 2 } },
    { id: 'ch3-zonnevlecht-zenuw', naam: 'Zonnevlecht Zenuw', type: 'o', cost: 2, origin: 3, tekst: 'Aanvallen -1 schade (2 beurten)', effect: { weakenBoss: { amt: 1, turns: 2 } } },
    { id: 'ch3-kernkracht', naam: 'Kernkracht', type: 'p', cost: 2, origin: 3, tekst: 'Zolang actief: +2 wilskracht/beurt', effect: { aura: { block: 2, name: 'Kernkracht' } } },
    { id: 'ch3-zonlicht-opnemen', naam: 'Zonlicht Opnemen', type: 'p', cost: 1, origin: 3, tekst: '+3 energie, baas -2 HP', effect: { energy: 3, dmg: 2 } },
    { id: 'ch3-grenzen-stellen', naam: 'Grenzen Stellen', type: 'p', cost: 2, origin: 3, tekst: 'Baas verliest 1 aanval permanent', effect: { removeAtk: true } },
    { id: 'ch3-wilskracht-training', naam: 'Wilskracht Training', type: 'p', cost: 2, origin: 3, tekst: 'Aanvallen +2 (2 beurten)', effect: { buffOutput: { amt: 2, turns: 2 } } },
    { id: 'ch3-doel-stellen', naam: 'Doel Stellen', type: 'p', cost: 1, origin: 3, tekst: 'Trek 2 kaarten, +1 energie', effect: { draw: 2, energy: 1 } },
    { id: 'ch3-vuur-element', naam: 'Vuur Element', type: 'e', cost: 3, origin: 3, tekst: 'Alle Manipura-kaarten ×2', effect: { mult: { origin: 3, amt: 2 } } },
    { id: 'ch3-innerlijk-vuur', naam: 'Innerlijk Vuur', type: 'e', cost: 2, origin: 3, tekst: 'Doorbreekt alle wilsblokkades', effect: { cleanse: 'all' }, meesterschap: true },
    { id: 'ch3-ram-mantra', naam: 'RAM Mantra', type: 'm', cost: 2, origin: 3, tekst: 'Doorbreekt vuurblokkade, +3 wilskracht', effect: { cleanse: 'one', block: 3, tone: 528 } },
    { id: 'ch3-ram-verdiept', naam: 'RAM Verdiept', type: 'm', cost: 3, origin: 3, tekst: 'Alle blokkades weg, +5 HP', effect: { cleanse: 'all', heal: 5, tone: 528 } },
    { id: 'ch3-geel-visualisatie', naam: 'Geel Visualisatie', type: 'k', cost: 1, origin: 3, tekst: '+2 energie, wilskrachtkaarten +1', effect: { energy: 2, buffNext: 1 } },
    { id: 'ch3-gele-ademhaling', naam: 'Gele Ademhaling', type: 'k', cost: 2, origin: 3, tekst: 'Trek 2 kaarten, baas -2 HP', effect: { draw: 2, dmg: 2 } },
  ],

  bazen: [
    {
      id: 'ch3-dravek',
      naam: 'Dravek',
      titel: 'De Domineerder',
      hp: 24,
      energie: 2,
      aanvallen: [
        { naam: 'Jij Doet Wat Ik Zeg', cost: 2, effect: { discard: { n: 1, mode: 'strongest' } } },
        { naam: 'Kleineren', cost: 1, effect: { dmg: 2, energyLoss: 2 } },
        { naam: 'Totale Overname', cost: 3, effect: { status: { kind: 'onlyType', dynamic: 'leastInHand', turns: 1 } } },
      ],
      zwaktes: [
        { card: 'Tijgeroog', counters: 'Kleineren' },
        { card: 'Wilskracht Training', counters: 'Totale Overname' },
        { card: 'Vuur Element', bonusDmg: 3 },
      ],
    },
    {
      id: 'ch3-ashvorn',
      naam: 'Ashvorn',
      titel: 'De Uitbrander',
      hp: 30,
      energie: 3,
      aanvallen: [
        { naam: 'Uitputting', cost: 2, effect: { energyLoss: 3 } },
        { naam: 'Lege Tank', cost: 3, effect: { status: { kind: 'maxPlay', amt: 2, turns: 2 } } },
        { naam: 'As en Stof', cost: 2, effect: { status: { kind: 'nullifyType', type: 'e', turns: 1 } } },
      ],
      zwaktes: [
        { card: 'Citrien', bonusEnergy: 3 },
        { card: 'Grenzen Stellen', removeAtk: true },
        { card: 'RAM Mantra', counters: 'As en Stof' },
      ],
    },
    {
      id: 'ch3-lureth',
      naam: 'Lureth',
      titel: 'De Zelfondermijner',
      hp: 28,
      energie: 2,
      aanvallen: [
        { naam: 'Niet Goed Genoeg', cost: 2, effect: { status: { kind: 'halfEffect', turns: 1 } } },
        { naam: 'Innerlijke Criticus', cost: 1, effect: { status: { kind: 'costUp', type: 'all', amt: 1, turns: 1 } } },
        { naam: 'Zelfdestructie', cost: 3, effect: { dmg: 5 } },
      ],
      zwaktes: [
        { card: 'Tijgeroog', counters: 'Niet Goed Genoeg', bonusBuff: 2 },
        { card: 'Wilskracht Training', bonusDmg: 5 },
        { card: 'Zonlicht Opnemen', counters: 'Innerlijke Criticus' },
      ],
    },
    {
      id: 'ch3-gravuun',
      naam: 'Gravuun',
      titel: 'De Machteloosheid Zelf',
      hp: 60,
      energie: 4,
      eindbaas: true,
      passief: 'Speelt de speler geen Steen-kaart in een beurt → −1 energie volgende beurt.',
      aanvallen: [
        { naam: 'Vuur Doven', cost: 3, effect: { status: { kind: 'lockType', type: 'e', turns: 2 } } },
        { naam: 'Wil Breken', cost: 4, effect: { status: { kind: 'noBossDmg', turns: 1 } } },
        { naam: 'Identiteitserosie', cost: 3, effect: { energyLoss: 3, discard: { n: 1, mode: 'strongest' } } },
        { naam: 'Grijze Sluier', cost: 2, effect: { status: { kind: 'debuffOutput', amt: 2, turns: 2 } } },
      ],
      combo: { bossHpLoss: 20 },
      zwaktes: [
        { card: 'RAM Mantra', counters: 'Vuur Doven' },
        { card: 'Kernkracht', counters: 'Wil Breken' },
      ],
      unlock: 'ch3-innerlijk-vuur',
    },
  ],
};
